import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
    getSalesReactBaseUrl,
    getLiveDataClients,
    getLiveDataBranches,
    resetLiveDataMasters,
    generateCounterPayloadLive,
    getConfirationBillsDetails,
    getSalesTrackingRecords,
    pushPosDataSync,
    getSmsSentDetails,
    getDaycloseDetails,
    getClientCountUpdation,
    getApplicationSettingLive,
    getMastersCreationLive,
    getOutletDetailsLive,
    getProductCategoryLive,
    getProductLive,
    getUsersLive,
    getCreditCustomerLive,
    getDataSynchingDetails,
    getBranchConfigurationDetailsLive,
    getCompanyDetailsAdmin,
    getLocationDetailsAdmin,
    getNotificationsLive
} from '../utilities/api';

const ENDPOINT_HANDLERS = {
    reset_post: resetLiveDataMasters,
    get_client_countupdation: getClientCountUpdation,
    Get_application_setting: getApplicationSettingLive,
    Get_masters_creation: getMastersCreationLive,
    Get_outlet_deatils: getOutletDetailsLive,
    Get_product_category: getProductCategoryLive,
    Get_product: getProductLive,
    Get_users: getUsersLive,
    Get_credit_customer: getCreditCustomerLive,
    generate_counter_payload: generateCounterPayloadLive,
    Confiration_bills_details: getConfirationBillsDetails,
    sales_tracking_records: getSalesTrackingRecords,
    get_companydetails_admin: getCompanyDetailsAdmin,
    get_locationdetails_admin: getLocationDetailsAdmin,
    Get_notifications: getNotificationsLive
};

const ENDPOINTS = [
    { key: 'reset_post', label: 'Reset All Masters', method: 'POST', path: 'api/server_masters/master_table_reset/{segment}', group: 'Masters Data Reset' },
    { key: 'get_client_countupdation', label: 'Get Client Count Updation', method: 'POST', path: 'api/live_data/get_client_countupdation/{segment}', group: 'Masters Data Reset' },
    { key: 'Get_application_setting', label: 'Get Application Setting', method: 'POST', path: 'api/live_data/Get_application_setting/{segment}', group: 'Masters Data Reset' },
    { key: 'Get_masters_creation', label: 'Get Masters Creation', method: 'POST', path: 'api/live_data/Get_masters_creation/{segment}', group: 'Masters Data Reset' },
    { key: 'Get_outlet_deatils', label: 'Get Outlet Details', method: 'POST', path: 'api/live_data/Get_outlet_deatils/{segment}', group: 'Masters Data Reset' },
    { key: 'Get_product_category', label: 'Get Product Category', method: 'POST', path: 'api/live_data/Get_product_category/{segment}', group: 'Masters Data Reset' },
    { key: 'Get_product', label: 'Get Product', method: 'POST', path: 'api/live_data/Get_product/{segment}', group: 'Masters Data Reset' },
    { key: 'Get_users', label: 'Get Users', method: 'POST', path: 'api/live_data/Get_users/{segment}', group: 'Masters Data Reset' },
    { key: 'Get_credit_customer', label: 'Get Credit Customer', method: 'POST', path: 'api/live_data/Get_credit_customer/{segment}', group: 'Masters Data Reset' },
    { key: 'generate_counter_payload', label: 'Generate Counter Payload', method: 'GET', path: 'api/live_data/generate_counter_payload/{segment}', group: 'Client Tracking' },
    { key: 'Confiration_bills_details', label: 'Configuration Bills Details', method: 'POST', path: 'api/live_data/Confiration_bills_details/{segment}', group: 'Client Tracking' },
    { key: 'sales_tracking_records', label: 'Sales Tracking Records (SMS, Day End, Data Synching)', method: 'GET', path: 'api/live_data/sales_tracking_records/{segment}', group: 'Client Tracking' },
    { key: 'get_companydetails_admin', label: 'Get Credit Customer', method: 'GET', path: 'api/server_masters/get_companydetails', group: 'Server Masters', paramsFromClient: true },
    { key: 'get_locationdetails_admin', label: 'Location', method: 'GET', path: 'api/server_masters/get_locationdetails', group: 'Server Masters', paramsFromClient: true },

    // Notifications live only in the master database, so these are always server-side —
    // there is no client-deployment equivalent to fall back to.
    { key: 'Get_notifications', label: 'Get Notifications', method: 'POST', path: 'api/live_data/Get_notifications/{segment}', group: 'Notifications' }
];

const GROUP_STYLES = {
    'Masters Data Reset': { color: '#be123c', bg: '#fff1f2', accent: '#f43f5e', soft: 'rgba(244, 63, 94, 0.10)', icon: 'pi pi-refresh' },
    'Client Tracking': { color: '#92400e', bg: '#fffbeb', accent: '#d97706', soft: 'rgba(217, 119, 6, 0.10)', icon: 'pi pi-satellite' },
    'Server Masters': { color: '#1d4ed8', bg: '#eff6ff', accent: '#3b82f6', soft: 'rgba(59, 130, 246, 0.10)', icon: 'pi pi-building' },
    Notifications: { color: '#6d28d9', bg: '#f5f3ff', accent: '#8b5cf6', soft: 'rgba(139, 92, 246, 0.10)', icon: 'pi pi-bell' }
};

const DEFAULT_GROUP_STYLE = { color: '#374151', bg: '#f9fafb', accent: '#9ca3af', soft: 'rgba(156, 163, 175, 0.12)', icon: 'pi pi-bookmark' };
const groupStyle = (group) => GROUP_STYLES[group] || DEFAULT_GROUP_STYLE;

/** Per-endpoint guidance shown before a run, for endpoints using the default layout. */
const ENDPOINT_HINTS = {
    Get_notifications:
        "Click Run to list the alerts still pending for the selected branch — the ones addressed to it plus the client-wide ones, newest first. notifications has no data_transfer column, so 'Closed' plays that role: everything not yet closed comes back, and api/live_data/callback_notifications/{branch} closes it. Expired alerts are held back whatever their status. An unknown branch answers with an empty list and an error field."
};

/**
 * Requests themselves go through the CRA dev proxy (relative path, same-origin, avoids CORS —
 * see getSalesReactBaseUrl). For display we want the real backend host, which the proxy's
 * `target` in setupProxy.js always points to, so the URL shown here is copy-pasteable/curl-able.
 */
const displayBaseUrl = () => (process.env.NODE_ENV === 'development' ? 'http://localhost/Counter-Backend' : getSalesReactBaseUrl());

const rawResultText = (data) => (typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data));

/** Compact status / method chip used across the list and detail pane. */
const Pill = ({ tone, children }) => <span className={`ld-pill ld-pill-${tone}`}>{children}</span>;

const StepHint = ({ children }) => (
    <div className="ld-hint">
        <i className="pi pi-info-circle" />
        <span>{children}</span>
    </div>
);

const StepLoading = ({ children }) => (
    <div className="ld-hint ld-hint-loading">
        <i className="pi pi-spin pi-spinner" />
        <span>{children}</span>
    </div>
);

/** Titles come through as "2. Day Close SMS Summary" — split the number out into its own badge. */
const StepTitle = ({ title }) => {
    const match = /^(\d+)\.\s*(.*)$/.exec(title);
    return (
        <div className="ld-step-title">
            {match ? (
                <>
                    <span className="ld-step-num">{match[1]}</span>
                    <span>{match[2]}</span>
                </>
            ) : (
                <span>{title}</span>
            )}
        </div>
    );
};

/** Status strip shared by the JSON viewer (dark) and the table renderers (light). */
const ResultMeta = ({ result, children }) => (
    <>
        <span className="ld-res-dot" />
        <span className="ld-res-code">{result.status === 'success' ? `${result.statusCode} OK` : `Error ${result.statusCode || ''}`}</span>
        <span className="ld-res-meta">
            <i className="pi pi-clock" /> {result.duration} ms
        </span>
        {result.testedAt && (
            <span className="ld-res-meta ld-hide-sm">
                <i className="pi pi-calendar" /> {result.testedAt}
            </span>
        )}
        <span className="ld-res-spacer" />
        {children}
    </>
);

/** Minimal, uniform block: title, then one combined row of request URL + action button, then status/JSON. */
const StepBlock = ({ title, action, method, url, loading, loadingText, result, emptyHint, customRender, maxHeight = 260 }) => {
    const [editMode, setEditMode] = useState(false);
    const [editedText, setEditedText] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setEditMode(false);
        setCopied(false);
    }, [result]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(editMode ? editedText : rawResultText(result.data));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // clipboard API unavailable — ignore
        }
    };

    const custom = result && customRender ? customRender(result) : null;
    const ok = result?.status === 'success';

    return (
        <div className="ld-step">
            {title && <StepTitle title={title} />}
            <div className="ld-reqbar">
                <span className={`ld-reqbar-method ld-method-${method.toLowerCase()}`}>{method}</span>
                <span className="ld-reqbar-url">{url}</span>
                <span className="ld-reqbar-action">{action}</span>
            </div>

            {loading && <StepLoading>{loadingText || 'Loading…'}</StepLoading>}

            {result ? (
                custom ? (
                    <div className={`ld-res is-light ${ok ? 'is-ok' : 'is-err'}`}>
                        <div className="ld-res-bar ld-res-bar-light">
                            <ResultMeta result={result} />
                        </div>
                        <div className="ld-table-wrap">{custom}</div>
                    </div>
                ) : (
                    <div className={`ld-res ${ok ? 'is-ok' : 'is-err'}`}>
                        <div className="ld-res-bar">
                            <ResultMeta result={result}>
                                <button type="button" className="ld-chip-btn" onClick={handleCopy}>
                                    <i className={copied ? 'pi pi-check' : 'pi pi-copy'} /> {copied ? 'Copied' : 'Copy'}
                                </button>
                                <button
                                    type="button"
                                    className="ld-chip-btn"
                                    onClick={() => {
                                        if (!editMode) setEditedText(rawResultText(result.data));
                                        setEditMode((v) => !v);
                                    }}
                                >
                                    <i className={editMode ? 'pi pi-eye' : 'pi pi-pencil'} /> {editMode ? 'View' : 'Edit'}
                                </button>
                            </ResultMeta>
                        </div>
                        {editMode ? (
                            <textarea className="ld-json-edit" value={editedText} onChange={(e) => setEditedText(e.target.value)} spellCheck={false} style={{ height: maxHeight }} />
                        ) : (
                            <pre className="ld-res-body" style={{ maxHeight }}>
                                {rawResultText(result.data)}
                            </pre>
                        )}
                    </div>
                )
            ) : (
                !loading && <StepHint>{emptyHint}</StepHint>
            )}
        </div>
    );
};

export default function LiveData() {
    const toast = useRef(null);

    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const branchCacheRef = useRef(new Map());

    const [results, setResults] = useState({});
    const [runningKeys, setRunningKeys] = useState(new Set());
    const [testingAll, setTestingAll] = useState(false);

    const [selectedEndpoint, setSelectedEndpoint] = useState(null);
    const [search, setSearch] = useState('');
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    const toggleGroup = (group) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(group)) next.delete(group);
            else next.add(group);
            return next;
        });
    };

    const [resetVisible, setResetVisible] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const [syncLoading, setSyncLoading] = useState(false);
    const [syncResult, setSyncResult] = useState(null);

    const [daycloseLoading, setDaycloseLoading] = useState(false);
    const [daycloseResult, setDaycloseResult] = useState(null);
    const [dayendLoading, setDayendLoading] = useState(false);
    const [dayendResult, setDayendResult] = useState(null);
    const [datasyncLoading, setDatasyncLoading] = useState(false);
    const [datasyncResult, setDatasyncResult] = useState(null);
    const [datasyncBreakdown, setDatasyncBreakdown] = useState(null);
    const [testSmsConfirm, setTestSmsConfirm] = useState(false);
    const [configLoading, setConfigLoading] = useState(false);
    const [configResult, setConfigResult] = useState(null);

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async () => {
        try {
            const data = await getLiveDataClients();
            setClients(data.map((c) => ({ label: c.client_name, value: c.client_id })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load clients' });
        }
    };

    const fetchBranches = async (clientId) => {
        const cached = branchCacheRef.current.get(clientId);
        if (cached) {
            setBranches(cached);
            return;
        }
        setBranchesLoading(true);
        try {
            const data = await getLiveDataBranches(clientId);
            const options = data.map((b) => ({ label: b.branch_name, value: b.branch_id }));
            branchCacheRef.current.set(clientId, options);
            setBranches(options);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load branches' });
        } finally {
            setBranchesLoading(false);
        }
    };

    const resolveUrl = (endpoint) => {
        if (endpoint.paramsFromClient) {
            const params = new URLSearchParams({ client_id: selectedClient || '', role: localStorage.getItem('role') || '' }).toString();
            return `${displayBaseUrl()}/${endpoint.path}?${params}`;
        }
        const branchObj = branches.find((b) => b.value === selectedBranch);
        const segment = branchObj ? branchObj.label : selectedBranch || '';
        return `${displayBaseUrl()}/${endpoint.path.replace('{segment}', segment)}`;
    };

    const runEndpoint = async (endpoint) => {
        const needsSegment = endpoint.path.includes('{segment}');
        if (needsSegment && !selectedBranch) {
            toast.current.show({ severity: 'warn', summary: 'Branch Required', detail: 'Please select a client and branch first' });
            return;
        }
        if (endpoint.paramsFromClient && !selectedClient) {
            toast.current.show({ severity: 'warn', summary: 'Client Required', detail: 'Please select a client first' });
            return;
        }

        setRunningKeys((prev) => new Set(prev).add(endpoint.key));
        const t0 = Date.now();

        try {
            const branchObj = branches.find((b) => b.value === selectedBranch);
            const segment = branchObj ? branchObj.label : selectedBranch || '';
            const data = endpoint.paramsFromClient
                ? await ENDPOINT_HANDLERS[endpoint.key]({ client_id: selectedClient, role: localStorage.getItem('role') || '' })
                : await ENDPOINT_HANDLERS[endpoint.key](segment);

            setResults((prev) => ({
                ...prev,
                [endpoint.key]: {
                    status: 'success',
                    statusCode: 200,
                    data,
                    duration: Date.now() - t0,
                    testedAt: new Date().toLocaleTimeString()
                }
            }));
        } catch (err) {
            setResults((prev) => ({
                ...prev,
                [endpoint.key]: {
                    status: 'error',
                    statusCode: err.response?.status || 0,
                    data: err.response?.data || err.message,
                    duration: Date.now() - t0,
                    testedAt: new Date().toLocaleTimeString()
                }
            }));
        } finally {
            setRunningKeys((prev) => {
                const n = new Set(prev);
                n.delete(endpoint.key);
                return n;
            });
        }
    };

    const runAll = async () => {
        if (!selectedBranch) {
            toast.current.show({ severity: 'warn', summary: 'Branch Required', detail: 'Please select a client and branch first' });
            return;
        }
        setTestingAll(true);
        for (const ep of ENDPOINTS) {
            if (ep.key === 'sales_tracking_records') continue;
            if (ep.group === 'Masters Data Reset') continue;
            await runEndpoint(ep);
        }
        setTestingAll(false);
        toast.current.show({ severity: 'success', summary: 'Done', detail: 'All endpoints tested' });
    };

    const resetMasters = async () => {
        const branchObj = branches.find((b) => b.value === selectedBranch);
        const segment = branchObj ? branchObj.label : '';
        setResetLoading(true);
        try {
            await resetLiveDataMasters(segment);
            toast.current.show({ severity: 'success', summary: 'Reset Successful', detail: `All masters reset for branch ${segment}` });
            setResetVisible(false);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Reset Failed', detail: err.response?.data?.message || 'Something went wrong' });
        } finally {
            setResetLoading(false);
        }
    };

    const pushSync = async () => {
        const branchObj = branches.find((b) => b.value === selectedBranch);
        const segment = branchObj ? branchObj.label : '';
        const payload = results['generate_counter_payload']?.data;
        setSyncLoading(true);
        setSyncResult(null);
        const t0 = Date.now();
        try {
            const data = await pushPosDataSync(segment, payload);
            setSyncResult({ status: 'success', statusCode: 200, data, duration: Date.now() - t0 });
        } catch (err) {
            setSyncResult({ status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || err.message, duration: Date.now() - t0 });
        } finally {
            setSyncLoading(false);
        }
    };

    const fetchSmsSummary = async () => {
        const branchObj = branches.find((b) => b.value === selectedBranch);
        const segment = branchObj ? branchObj.label : '';
        setDaycloseLoading(true);
        setDaycloseResult(null);
        const t0 = Date.now();
        try {
            const data = await getSmsSentDetails(segment);
            setDaycloseResult({ status: 'success', statusCode: 200, data, duration: Date.now() - t0 });
        } catch (err) {
            setDaycloseResult({ status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || err.message, duration: Date.now() - t0 });
        } finally {
            setDaycloseLoading(false);
        }
    };

    const fetchDayendDetails = async () => {
        const branchObj = branches.find((b) => b.value === selectedBranch);
        const segment = branchObj ? branchObj.label : '';
        setDayendLoading(true);
        setDayendResult(null);
        const t0 = Date.now();
        try {
            const data = await getDaycloseDetails(segment);
            setDayendResult({ status: 'success', statusCode: 200, data, duration: Date.now() - t0 });
        } catch (err) {
            setDayendResult({ status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || err.message, duration: Date.now() - t0 });
        } finally {
            setDayendLoading(false);
        }
    };

    const fetchDataSyncDetails = async () => {
        const branchObj = branches.find((b) => b.value === selectedBranch);
        const segment = branchObj ? branchObj.label : '';
        setDatasyncLoading(true);
        setDatasyncResult(null);
        const t0 = Date.now();
        try {
            const data = await getDataSynchingDetails(segment);
            setDatasyncResult({ status: 'success', statusCode: 200, data, duration: Date.now() - t0 });
        } catch (err) {
            setDatasyncResult({ status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || err.message, duration: Date.now() - t0 });
        } finally {
            setDatasyncLoading(false);
        }
    };

    const fetchConfigDetails = async () => {
        const branchObj = branches.find((b) => b.value === selectedBranch);
        const segment = branchObj ? branchObj.label : '';
        setConfigLoading(true);
        setConfigResult(null);
        const t0 = Date.now();
        try {
            const data = await getBranchConfigurationDetailsLive(segment);
            setConfigResult({ status: 'success', statusCode: 200, data, duration: Date.now() - t0 });
        } catch (err) {
            setConfigResult({ status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || err.message, duration: Date.now() - t0 });
        } finally {
            setConfigLoading(false);
        }
    };

    const selectEndpoint = (endpoint) => {
        setSelectedEndpoint(endpoint);
        setSyncResult(null);
        setDaycloseResult(null);
        setDayendResult(null);
        setDatasyncResult(null);
        setDatasyncBreakdown(null);
        setConfigResult(null);
    };

    const successCount = Object.values(results).filter((r) => r.status === 'success').length;
    const failCount = Object.values(results).filter((r) => r.status === 'error').length;
    const notTested = ENDPOINTS.length - Object.keys(results).length;
    const coverage = Math.round((Object.keys(results).length / ENDPOINTS.length) * 100);

    const groupedEndpoints = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = q ? ENDPOINTS.filter((e) => e.label.toLowerCase().includes(q)) : ENDPOINTS;
        const groups = {};
        filtered.forEach((e) => {
            groups[e.group] = groups[e.group] || [];
            groups[e.group].push(e);
        });
        return groups;
    }, [search]);

    const statusInfo = (rowData) => {
        if (runningKeys.has(rowData.key)) return { text: 'Running…', severity: 'info' };
        const r = results[rowData.key];
        if (!r) return { text: 'Not Tested', severity: 'secondary' };
        return r.status === 'success' ? { text: `${r.statusCode} OK`, severity: 'success' } : { text: `Error ${r.statusCode || ''}`, severity: 'danger' };
    };

    const dialogResult = selectedEndpoint ? results[selectedEndpoint.key] : null;
    const branchLabel = branches.find((b) => b.value === selectedBranch)?.label;

    const renderDetailBody = () => {
        if (!selectedEndpoint) return null;

        if (selectedEndpoint.key === 'sales_tracking_records') {
            return (
                <>
                    <StepBlock
                        title="1. Fetch Sales Tracking Records"
                        method="GET"
                        url={resolveUrl(selectedEndpoint)}
                        action={<Button label="Test SMS" icon="pi pi-play" className="p-button-sm" onClick={() => setTestSmsConfirm(true)} loading={runningKeys.has(selectedEndpoint.key)} />}
                        result={dialogResult}
                        emptyHint="Click Test SMS to load the sales tracking data."
                    />

                    <StepBlock
                        title="2. Day Close SMS Summary"
                        method="GET"
                        url={`${displayBaseUrl()}/api/live_data/sms_sentdetails/${branchLabel || ''}`}
                        action={<Button label={daycloseLoading ? 'Loading…' : 'Fetch SMS Summary'} icon="pi pi-envelope" className="p-button-sm" onClick={fetchSmsSummary} loading={daycloseLoading} />}
                        loading={daycloseLoading}
                        loadingText="Fetching SMS summary…"
                        result={daycloseResult}
                        emptyHint="Click Fetch SMS Summary to load the day close SMS details."
                        customRender={(result) => {
                            if (result.status !== 'success') return null;
                            const raw = result.data;
                            const rows = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
                            const fmtDate = (d) => {
                                if (!d) return '';
                                const [y, m, day] = d.split('-');
                                return `${day}-${m}-${y}`;
                            };
                            return rows.length > 0 ? (
                                <DataTable value={rows} showGridlines className="p-datatable-sm" scrollable scrollHeight="220px" style={{ fontSize: '0.78rem' }}>
                                    <Column header="Date & Time" body={(r) => `${fmtDate(r.requested_date)} ${r.requested_time || ''}`.trim()} style={{ minWidth: '160px' }} />
                                    <Column field="sent_status" header="Sent Status" style={{ minWidth: '100px' }} />
                                    <Column field="delivery_status" header="Delivery Status" style={{ minWidth: '120px' }} />
                                    <Column field="local_status" header="Local Status" style={{ minWidth: '100px' }} />
                                </DataTable>
                            ) : (
                                <div className="ld-table-empty">No records found in response.</div>
                            );
                        }}
                    />

                    <StepBlock
                        title="3. Day End Details"
                        method="GET"
                        url={`${displayBaseUrl()}/api/live_data/dayclose_details/${branchLabel || ''}`}
                        action={<Button label="Fetch Day End Details" icon="pi pi-sun" className="p-button-sm" onClick={fetchDayendDetails} loading={dayendLoading} />}
                        loading={dayendLoading}
                        loadingText="Fetching day end details…"
                        result={dayendResult}
                        emptyHint="Click Fetch Day End Details to load day end summary."
                        customRender={(result) => {
                            if (result.status !== 'success') return null;
                            const raw = result.data;
                            const rows = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
                            const cols = rows.length > 0 ? Object.keys(rows[0]) : [];
                            const fmtDate = (v) => {
                                if (!v) return '—';
                                const parts = String(v).split('-');
                                return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : v;
                            };
                            const isDateCol = (col) => /date/i.test(col);
                            const cellVal = (row, col) => {
                                const v = row[col];
                                if (v === null || v === undefined) return '—';
                                if (typeof v === 'object') return JSON.stringify(v);
                                if (isDateCol(col)) return fmtDate(v);
                                return String(v);
                            };
                            if (cols.length === 0) return null;
                            return (
                                <DataTable value={rows} showGridlines className="p-datatable-sm" scrollable scrollHeight="200px" style={{ fontSize: '0.78rem' }}>
                                    {cols.map((col) => (
                                        <Column key={col} header={col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} body={(row) => cellVal(row, col)} style={{ minWidth: '110px' }} />
                                    ))}
                                </DataTable>
                            );
                        }}
                    />

                    <StepBlock
                        title="4. Data Synching Tracking"
                        method="GET"
                        url={`${displayBaseUrl()}/api/live_data/Data_sychingdetails/${branchLabel || ''}`}
                        action={<Button label="Fetch Sync Details" icon="pi pi-sync" className="p-button-sm" onClick={fetchDataSyncDetails} loading={datasyncLoading} />}
                        loading={datasyncLoading}
                        loadingText="Fetching data synching details…"
                        result={datasyncResult}
                        emptyHint="Click Fetch Sync Details to load data synching tracking."
                        customRender={(result) => {
                            if (result.status !== 'success') return null;
                            const raw = result.data;
                            const rawRows = Array.isArray(raw?.bills) ? raw.bills : Array.isArray(raw) ? raw : [];
                            const rows = [...rawRows].sort((a, b) => new Date(a.invoice_date) - new Date(b.invoice_date));
                            const fmtDate = (v) => {
                                if (!v) return '—';
                                const p = String(v).split('-');
                                return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : v;
                            };
                            const fmtDatetime = (v) => {
                                if (!v) return '—';
                                const [dt, tm] = String(v).split(' ');
                                const p = (dt || '').split('-');
                                return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]} ${tm || ''}`.trim() : v;
                            };
                            if (rows.length === 0) return <div className="ld-table-empty">No records found in response.</div>;
                            return (
                                <>
                                    <DataTable value={rows} showGridlines className="p-datatable-sm" scrollable scrollHeight="220px" style={{ fontSize: '0.78rem' }}>
                                        <Column header="Invoice Date" body={(r) => fmtDate(r.invoice_date)} style={{ minWidth: '110px' }} />
                                        <Column header="Invoice Time" field="invoice_time" style={{ minWidth: '100px' }} />
                                        <Column header="Start Bill ID" field="starting_bill_id" style={{ minWidth: '105px' }} />
                                        <Column header="End Bill ID" field="end_bill_id" style={{ minWidth: '100px' }} />
                                        <Column header="Start Invoice No" field="starting_invoice_no" style={{ minWidth: '140px' }} />
                                        <Column header="End Invoice No" field="end_invoice_no" style={{ minWidth: '140px' }} />
                                        <Column header="Total Paid" body={(r) => (
                                                <span className="ld-link-amount" onClick={() => setDatasyncBreakdown(r.payment_breakdown)} title="Click to view payment breakdown">
                                                    {r.total_paid_amount}
                                                </span>
                                            )}
                                            style={{ minWidth: '100px' }}
                                        />
                                        <Column header="Start Synching" body={(r) => fmtDatetime(r.created)} style={{ minWidth: '150px' }} />
                                        <Column header="Last Synched" body={(r) => fmtDatetime(r.updated)} style={{ minWidth: '150px' }} />
                                    </DataTable>
                                    {datasyncBreakdown && (
                                        <div className="ld-breakdown">
                                            <div className="ld-breakdown-head">
                                                <span>
                                                    <i className="pi pi-credit-card" /> Payment Breakdown
                                                </span>
                                                <span className="ld-breakdown-close" onClick={() => setDatasyncBreakdown(null)}>
                                                    <i className="pi pi-times" /> Close
                                                </span>
                                            </div>
                                            <DataTable value={datasyncBreakdown} showGridlines className="p-datatable-sm" style={{ fontSize: '0.78rem' }}>
                                                <Column field="payment_type" header="Payment Type" style={{ minWidth: '120px' }} />
                                                <Column field="amount" header="Amount" style={{ minWidth: '100px' }} />
                                            </DataTable>
                                        </div>
                                    )}
                                </>
                            );
                        }}
                    />
                </>
            );
        }

        if (selectedEndpoint.key === 'generate_counter_payload') {
            return (
                <>
                    <StepBlock
                        title="1. Generate Payload"
                        method="GET"
                        url={resolveUrl(selectedEndpoint)}
                        action={<Button label="Generate" icon="pi pi-play" className="p-button-sm" onClick={() => runEndpoint(selectedEndpoint)} loading={runningKeys.has(selectedEndpoint.key)} />}
                        result={dialogResult}
                        emptyHint="Click Generate to fetch and preview the counter payload."
                    />
                    <StepBlock
                        title="2. Check Syncing"
                        method="POST"
                        url={`${displayBaseUrl()}/api/live_data/Pos_data_synching/${branchLabel || ''}`}
                        action={<Button label="Push & Sync" icon="pi pi-cloud-upload" className="p-button-sm" onClick={pushSync} loading={syncLoading} disabled={!dialogResult} />}
                        loading={syncLoading}
                        loadingText="Pushing payload to sync…"
                        result={syncResult}
                        emptyHint="Complete Step 1 first, then click Push & Sync to verify data syncing."
                    />
                </>
            );
        }

        if (selectedEndpoint.key === 'Confiration_bills_details') {
            return (
                <>
                    <StepBlock
                        title="1. Configuration Bills Details"
                        method="POST"
                        url={resolveUrl(selectedEndpoint)}
                        action={<Button label="Fetch Bills Details" icon="pi pi-play" className="p-button-sm" onClick={() => runEndpoint(selectedEndpoint)} loading={runningKeys.has(selectedEndpoint.key)} />}
                        result={dialogResult}
                        emptyHint="Click Fetch Bills Details to load configuration bill data."
                    />
                    <StepBlock
                        title="2. Branch Configuration Details"
                        method="GET"
                        url={`${displayBaseUrl()}/api/live_data/branch_configrationdetails/${branchLabel || ''}`}
                        action={<Button label="Get Config Details" icon="pi pi-download" className="p-button-sm" onClick={fetchConfigDetails} loading={configLoading} />}
                        loading={configLoading}
                        loadingText="Fetching configuration details…"
                        result={configResult}
                        emptyHint="Click Get Config Details to fetch the branch configuration."
                    />
                </>
            );
        }

        /* ── Default layout for any future simple endpoints ── */
        return (
            <StepBlock
                method={selectedEndpoint.method}
                url={resolveUrl(selectedEndpoint)}
                action={<Button label="Run" icon="pi pi-play" className="p-button-sm" onClick={() => runEndpoint(selectedEndpoint)} loading={runningKeys.has(selectedEndpoint.key)} />}
                result={dialogResult}
                emptyHint={ENDPOINT_HINTS[selectedEndpoint.key] || 'Click Run to test this endpoint.'}
                maxHeight={320}
            />
        );
    };

    const detailStyle = selectedEndpoint ? groupStyle(selectedEndpoint.group) : DEFAULT_GROUP_STYLE;

    return (
        <div className="livedata-page">
            <Toast ref={toast} />
            <style>{`
                .livedata-page { padding: 1.25rem; background: #f4f5fa; min-height: 100%; }

                /* ── Hero ───────────────────────────────────────────── */
                .livedata-page .ld-hero { position: relative; overflow: hidden; border-radius: 18px; padding: 22px 26px; margin-bottom: 16px;
                    background: linear-gradient(135deg, #4c1d95 0%, #5b2fa0 45%, #7c3aed 100%);
                    box-shadow: 0 12px 30px -12px rgba(76, 29, 149, 0.55); display: flex; gap: 24px; align-items: center; flex-wrap: wrap; }
                .livedata-page .ld-hero::after { content: ''; position: absolute; right: -70px; top: -110px; width: 320px; height: 320px; border-radius: 50%;
                    background: radial-gradient(circle, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 68%); pointer-events: none; }
                .livedata-page .ld-hero-main { display: flex; align-items: center; gap: 16px; flex: 1; min-width: 280px; position: relative; z-index: 1; }
                .livedata-page .ld-hero-icon { width: 52px; height: 52px; flex-shrink: 0; border-radius: 14px; display: flex; align-items: center; justify-content: center;
                    background: rgba(255,255,255,0.16); border: 1px solid rgba(255,255,255,0.25); backdrop-filter: blur(6px); }
                .livedata-page .ld-hero-icon i { font-size: 1.5rem; color: #fff; }
                .livedata-page .ld-hero-title { margin: 0; font-size: 1.4rem; font-weight: 800; color: #fff; letter-spacing: -0.02em; line-height: 1.2; }
                .livedata-page .ld-hero-sub { margin: 5px 0 0; font-size: 0.82rem; color: rgba(255,255,255,0.78); max-width: 640px; line-height: 1.45; }

                .livedata-page .ld-stats { display: flex; gap: 10px; flex-wrap: wrap; position: relative; z-index: 1; }
                .livedata-page .ld-stat { min-width: 96px; padding: 10px 14px; border-radius: 12px; background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.18); backdrop-filter: blur(8px); transition: transform .18s ease, background-color .18s ease; }
                .livedata-page .ld-stat:hover { transform: translateY(-2px); background: rgba(255,255,255,0.18); }
                .livedata-page .ld-stat-value { font-size: 1.4rem; font-weight: 800; color: #fff; line-height: 1.1; display: flex; align-items: center; gap: 7px; }
                .livedata-page .ld-stat-value i { font-size: 0.8rem; }
                .livedata-page .ld-stat-label { font-size: 0.63rem; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: rgba(255,255,255,0.7); margin-top: 3px; }
                .livedata-page .ld-stat.ok .ld-stat-value i { color: #6ee7b7; }
                .livedata-page .ld-stat.err .ld-stat-value i { color: #fda4af; }
                .livedata-page .ld-stat.idle .ld-stat-value i { color: #cbd5e1; }
                .livedata-page .ld-stat.all .ld-stat-value i { color: #c4b5fd; }
                .livedata-page .ld-progress { height: 4px; border-radius: 4px; background: rgba(255,255,255,0.2); margin-top: 8px; overflow: hidden; }
                .livedata-page .ld-progress span { display: block; height: 100%; border-radius: 4px; background: linear-gradient(90deg, #a78bfa, #6ee7b7); transition: width .4s ease; }

                /* ── Toolbar ────────────────────────────────────────── */
                .livedata-page .ld-toolbar { background: #fff; border: 1px solid #e8eaf2; border-radius: 16px; padding: 14px 18px; margin-bottom: 16px;
                    box-shadow: 0 2px 10px -4px rgba(15, 23, 42, 0.10); display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap; }
                .livedata-page .ld-field { display: flex; flex-direction: column; flex: 1; min-width: 210px; }
                .livedata-page .ld-field-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #7c3aed; margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
                .livedata-page .ld-field-label .ld-muted { color: #a1a1b5; font-weight: 600; letter-spacing: 0.04em; text-transform: none; }
                .livedata-page .ld-field .p-dropdown { border-radius: 10px; border-color: #e2e5ef; }
                .livedata-page .ld-field .p-dropdown:not(.p-disabled):hover { border-color: #c4b5fd; }
                .livedata-page .ld-field .p-dropdown:not(.p-disabled).p-focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.14); }
                .livedata-page .ld-segment { display: inline-flex; align-items: center; gap: 7px; padding: 8px 13px; border-radius: 10px; background: #f5f3ff;
                    border: 1px dashed #c4b5fd; color: #5b21b6; font-size: 0.78rem; font-weight: 600; font-family: ui-monospace, Menlo, Consolas, monospace; white-space: nowrap; }
                .livedata-page .ld-segment i { font-size: 0.75rem; }
                .livedata-page .ld-testall.p-button { border-radius: 10px; font-weight: 600; border: none;
                    background: linear-gradient(135deg, #6d28d9, #7c3aed); box-shadow: 0 6px 14px -6px rgba(109, 40, 217, 0.8); }
                .livedata-page .ld-testall.p-button:enabled:hover { background: linear-gradient(135deg, #5b21b6, #6d28d9); }
                .livedata-page .ld-testall.p-button:disabled { background: #cbd5e1; box-shadow: none; opacity: 1; }
                .livedata-page .ld-clear.p-button { border-radius: 10px; background: #fff; border: 1px solid #e2e5ef; color: #64748b; font-weight: 600; }
                .livedata-page .ld-clear.p-button:enabled:hover { background: #f8fafc; border-color: #cbd5e1; color: #475569; }

                /* ── Split view ─────────────────────────────────────── */
                .livedata-page .ld-split { display: flex; gap: 16px; height: 70vh; min-height: 500px; }
                .livedata-page .ld-sidebar, .livedata-page .ld-detail { background: #fff; border: 1px solid #e8eaf2; border-radius: 16px;
                    box-shadow: 0 2px 10px -4px rgba(15, 23, 42, 0.10); overflow-y: auto; }
                .livedata-page .ld-sidebar { width: 340px; flex-shrink: 0; }
                .livedata-page .ld-detail { flex: 1; display: flex; flex-direction: column; }
                .livedata-page .ld-sidebar::-webkit-scrollbar, .livedata-page .ld-detail::-webkit-scrollbar, .livedata-page .ld-res-body::-webkit-scrollbar { width: 8px; height: 8px; }
                .livedata-page .ld-sidebar::-webkit-scrollbar-thumb, .livedata-page .ld-detail::-webkit-scrollbar-thumb { background: #dfe3ec; border-radius: 8px; }
                .livedata-page .ld-sidebar::-webkit-scrollbar-thumb:hover, .livedata-page .ld-detail::-webkit-scrollbar-thumb:hover { background: #c7cddb; }
                .livedata-page .ld-res-body::-webkit-scrollbar-thumb { background: #334155; border-radius: 8px; }

                /* Search */
                .livedata-page .ld-search { position: sticky; top: 0; z-index: 3; background: #fff; padding: 12px; border-bottom: 1px solid #eef0f6; }
                .livedata-page .ld-search .p-input-icon-left { width: 100%; }
                .livedata-page .ld-search .p-inputtext { width: 100%; border-radius: 10px; background: #f7f8fc; border-color: #e8eaf2; font-size: 0.85rem; padding-left: 2.4rem; }
                .livedata-page .ld-search .p-inputtext:enabled:focus { background: #fff; border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12); }
                .livedata-page .ld-search i { color: #a5abbd; }

                /* Group headers */
                .livedata-page .ld-group-header { padding: 9px 14px; font-weight: 800; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.09em;
                    position: sticky; top: 61px; z-index: 2; display: flex; align-items: center; gap: 8px; user-select: none; cursor: pointer; transition: filter .15s ease; }
                .livedata-page .ld-group-header:hover { filter: brightness(0.97); }
                .livedata-page .ld-group-count { font-weight: 700; font-size: 0.68rem; padding: 1px 8px; border-radius: 999px; background: rgba(255,255,255,0.72); }

                /* List items */
                .livedata-page .ld-item { cursor: pointer; padding: 11px 14px 11px 15px; border-left: 3px solid transparent; border-bottom: 1px solid #f1f3f8;
                    transition: background-color .15s ease, border-color .15s ease, padding-left .15s ease; }
                .livedata-page .ld-item:hover { background: #f8f9fd; padding-left: 18px; }
                .livedata-page .ld-item.selected { background: linear-gradient(90deg, #f5f3ff 0%, #fbfaff 100%); }
                .livedata-page .ld-item.selected .ld-item-title { color: #5b21b6; }
                .livedata-page .ld-item-row { display: flex; align-items: center; gap: 8px; }
                .livedata-page .ld-item-title { font-weight: 600; font-size: 0.85rem; color: #1e293b; flex: 1; line-height: 1.3; }
                .livedata-page .ld-item-meta { font-size: 0.7rem; color: #a5abbd; }
                .livedata-page .ld-item-foot { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
                .livedata-page .ld-empty-search { padding: 26px 16px; text-align: center; color: #a5abbd; font-size: 0.83rem; }

                /* Pills */
                .livedata-page .ld-pill { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 999px;
                    font-size: 0.63rem; font-weight: 700; letter-spacing: 0.03em; line-height: 1.6; border: 1px solid transparent; white-space: nowrap; }
                .livedata-page .ld-pill-get { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
                .livedata-page .ld-pill-post { background: #fff7ed; color: #b45309; border-color: #fed7aa; }
                .livedata-page .ld-pill-success { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
                .livedata-page .ld-pill-danger { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
                .livedata-page .ld-pill-secondary { background: #f4f5f9; color: #64748b; border-color: #e2e5ef; }
                .livedata-page .ld-pill-info { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }

                /* Detail header */
                .livedata-page .ld-detail-header { padding: 18px 24px; border-bottom: 1px solid #eef0f6; position: sticky; top: 0; z-index: 2; backdrop-filter: blur(6px); }
                .livedata-page .ld-crumb { display: flex; align-items: center; gap: 8px; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 8px; }
                .livedata-page .ld-detail-title { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
                .livedata-page .ld-detail-title h3 { margin: 0; font-size: 1.28rem; font-weight: 800; color: #1e293b; letter-spacing: -0.02em; }
                .livedata-page .ld-detail-path { margin-top: 8px; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.74rem; color: #94a3b8; word-break: break-all; }
                .livedata-page .ld-detail-body { padding: 18px 24px 24px; }

                /* Steps */
                .livedata-page .ld-step + .ld-step { margin-top: 20px; padding-top: 20px; border-top: 1px dashed #e8eaf2; }
                .livedata-page .ld-step-title { display: flex; align-items: center; gap: 10px; font-weight: 600; font-size: 0.86rem; color: #334155; margin-bottom: 10px; }
                .livedata-page .ld-step-num { width: 22px; height: 22px; flex-shrink: 0; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
                    background: linear-gradient(135deg, #6d28d9, #8b5cf6); color: #fff; font-size: 0.7rem; font-weight: 800; box-shadow: 0 3px 8px -3px rgba(109, 40, 217, 0.8); }
                .livedata-page .ld-hint { display: flex; align-items: flex-start; gap: 9px; margin-top: 12px; padding: 12px 14px; border-radius: 10px;
                    background: #f8f9fd; border: 1px dashed #e2e5ef; color: #7c8598; font-size: 0.8rem; line-height: 1.5; }
                .livedata-page .ld-hint i { color: #a78bfa; margin-top: 2px; }
                .livedata-page .ld-hint-loading { background: #f5f3ff; border-color: #ddd6fe; color: #6d28d9; }

                /* Request bar */
                .livedata-page .ld-reqbar { display: flex; align-items: stretch; border: 1px solid #e2e5ef; border-radius: 12px; overflow: hidden;
                    background: #fbfbfe; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05); }
                .livedata-page .ld-reqbar-method { display: flex; align-items: center; padding: 0 15px; font-weight: 800; font-size: 0.74rem;
                    font-family: ui-monospace, Menlo, Consolas, monospace; letter-spacing: 0.06em; border-right: 1px solid #e2e5ef; white-space: nowrap; }
                .livedata-page .ld-method-get { color: #047857; background: #ecfdf5; }
                .livedata-page .ld-method-post { color: #b45309; background: #fff7ed; }
                .livedata-page .ld-reqbar-url { flex: 1; display: flex; align-items: center; padding: 11px 14px; font-family: ui-monospace, Menlo, Consolas, monospace;
                    font-size: 0.78rem; color: #475569; word-break: break-all; min-width: 0; }
                .livedata-page .ld-reqbar-action { display: flex; align-items: stretch; }
                .livedata-page .ld-reqbar-action .p-button { height: 100%; border-radius: 0 !important; padding-left: 18px; padding-right: 18px; font-weight: 600;
                    background: linear-gradient(135deg, #6d28d9, #7c3aed) !important; border: none !important; box-shadow: none !important; }
                .livedata-page .ld-reqbar-action .p-button:enabled:hover { background: linear-gradient(135deg, #5b21b6, #6d28d9) !important; }
                .livedata-page .ld-reqbar-action .p-button:disabled { background: #cbd5e1 !important; opacity: 1; }

                /* Response viewer */
                .livedata-page .ld-res { margin-top: 12px; border-radius: 12px; overflow: hidden; border: 1px solid #1e293b; }
                .livedata-page .ld-res.is-light { border-color: #e2e5ef; }
                .livedata-page .ld-res-bar { display: flex; align-items: center; gap: 12px; padding: 8px 13px; background: #111827; }
                .livedata-page .ld-res-bar-light { background: #f8f9fd; border-bottom: 1px solid #eef0f6; }
                .livedata-page .ld-res-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
                .livedata-page .ld-res.is-ok .ld-res-dot { background: #34d399; box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.2); }
                .livedata-page .ld-res.is-err .ld-res-dot { background: #fb7185; box-shadow: 0 0 0 3px rgba(251, 113, 133, 0.2); }
                .livedata-page .ld-res-code { font-size: 0.74rem; font-weight: 800; letter-spacing: 0.04em; font-family: ui-monospace, Menlo, Consolas, monospace; }
                .livedata-page .ld-res.is-ok .ld-res-code { color: #6ee7b7; }
                .livedata-page .ld-res.is-err .ld-res-code { color: #fda4af; }
                .livedata-page .ld-res.is-light.is-ok .ld-res-code { color: #047857; }
                .livedata-page .ld-res.is-light.is-err .ld-res-code { color: #b91c1c; }
                .livedata-page .ld-res-meta { display: inline-flex; align-items: center; gap: 5px; font-size: 0.7rem; color: #94a3b8; }
                .livedata-page .ld-res-meta i { font-size: 0.68rem; }
                .livedata-page .ld-res-spacer { flex: 1; }
                .livedata-page .ld-chip-btn { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 7px; cursor: pointer;
                    background: rgba(148, 163, 184, 0.14); border: 1px solid rgba(148, 163, 184, 0.2); color: #cbd5e1; font-size: 0.68rem; font-weight: 600; transition: background-color .15s ease; }
                .livedata-page .ld-chip-btn:hover { background: rgba(148, 163, 184, 0.26); color: #fff; }
                .livedata-page .ld-res-body { margin: 0; padding: 13px 15px; background: #0f172a; color: #e2e8f0; overflow: auto;
                    font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.76rem; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }
                .livedata-page .ld-json-edit { width: 100%; resize: vertical; box-sizing: border-box; display: block; border: none; outline: none;
                    background: #0f172a; color: #e2e8f0; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.78rem; line-height: 1.6; padding: 13px 15px; }
                .livedata-page .ld-json-edit::selection { background: rgba(124, 58, 237, 0.45); }

                /* Tables inside results */
                .livedata-page .ld-table-wrap { padding: 12px; background: #fff; }
                .livedata-page .ld-table-wrap .p-datatable .p-datatable-thead > tr > th { background: #f5f3ff; color: #5b21b6; font-size: 0.72rem; font-weight: 700;
                    text-transform: uppercase; letter-spacing: 0.05em; padding: 8px 10px; border-color: #eae7f7; }
                .livedata-page .ld-table-wrap .p-datatable .p-datatable-tbody > tr > td { padding: 7px 10px; border-color: #f1f3f8; color: #475569; }
                .livedata-page .ld-table-wrap .p-datatable .p-datatable-tbody > tr:hover { background: #fbfaff; }
                .livedata-page .ld-table-empty { padding: 18px; text-align: center; color: #a5abbd; font-size: 0.82rem; }
                .livedata-page .ld-link-amount { color: #6d28d9; font-weight: 700; cursor: pointer; text-decoration: underline dotted; }
                .livedata-page .ld-link-amount:hover { color: #4c1d95; }
                .livedata-page .ld-breakdown { margin-top: 12px; border: 1px solid #e0d9fb; border-radius: 12px; overflow: hidden; background: #faf9ff; }
                .livedata-page .ld-breakdown-head { display: flex; justify-content: space-between; align-items: center; padding: 9px 13px;
                    background: #f5f3ff; border-bottom: 1px solid #e0d9fb; font-weight: 700; font-size: 0.8rem; color: #5b21b6; }
                .livedata-page .ld-breakdown-head i { font-size: 0.78rem; margin-right: 5px; }
                .livedata-page .ld-breakdown-close { cursor: pointer; color: #94a3b8; font-size: 0.75rem; font-weight: 600; }
                .livedata-page .ld-breakdown-close:hover { color: #475569; }
                .livedata-page .ld-breakdown .p-datatable { padding: 10px; }

                /* Empty detail state */
                .livedata-page .ld-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; text-align: center; padding: 40px; }
                .livedata-page .ld-empty-icon { width: 88px; height: 88px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
                    background: linear-gradient(135deg, #f5f3ff, #ede9fe); margin-bottom: 18px; }
                .livedata-page .ld-empty-icon i { font-size: 2.2rem; color: #a78bfa; }
                .livedata-page .ld-empty h4 { margin: 0 0 6px; font-size: 1.02rem; font-weight: 700; color: #475569; }
                .livedata-page .ld-empty p { margin: 0; font-size: 0.84rem; color: #94a3b8; max-width: 380px; line-height: 1.55; }

                @media (max-width: 1100px) {
                    .livedata-page .ld-split { flex-direction: column; height: auto; }
                    .livedata-page .ld-sidebar { width: 100%; max-height: 360px; }
                    .livedata-page .ld-detail { min-height: 440px; }
                }
                @media (max-width: 640px) {
                    .livedata-page { padding: 0.75rem; }
                    .livedata-page .ld-hero { padding: 18px; }
                    .livedata-page .ld-stat { flex: 1; min-width: 78px; }
                    .livedata-page .ld-reqbar { flex-direction: column; align-items: stretch; }
                    .livedata-page .ld-reqbar-method { border-right: none; border-bottom: 1px solid #e2e5ef; padding: 8px 14px; }
                    .livedata-page .ld-reqbar-action .p-button { width: 100%; }
                    .livedata-page .ld-hide-sm { display: none; }
                    .livedata-page .ld-detail-header, .livedata-page .ld-detail-body { padding-left: 16px; padding-right: 16px; }
                }
            `}</style>

            {/* ── Hero ───────────────────────────────────────────── */}
            <div className="ld-hero">
                <div className="ld-hero-main">
                    <div className="ld-hero-icon">
                        <i className="pi pi-database" />
                    </div>
                    <div>
                        <h2 className="ld-hero-title">Live Data — Endpoint Tester</h2>
                        <p className="ld-hero-sub">Select a client and branch, then pick an endpoint on the left — request URL, actions and live response appear on the right.</p>
                    </div>
                </div>

                <div className="ld-stats">
                    <div className="ld-stat ok">
                        <div className="ld-stat-value">
                            <i className="pi pi-check-circle" />
                            {successCount}
                        </div>
                        <div className="ld-stat-label">Success</div>
                    </div>
                    <div className="ld-stat err">
                        <div className="ld-stat-value">
                            <i className="pi pi-times-circle" />
                            {failCount}
                        </div>
                        <div className="ld-stat-label">Failed</div>
                    </div>
                    <div className="ld-stat idle">
                        <div className="ld-stat-value">
                            <i className="pi pi-minus-circle" />
                            {notTested}
                        </div>
                        <div className="ld-stat-label">Not Tested</div>
                    </div>
                    <div className="ld-stat all">
                        <div className="ld-stat-value">
                            <i className="pi pi-bolt" />
                            {ENDPOINTS.length}
                        </div>
                        <div className="ld-stat-label">Endpoints</div>
                        <div className="ld-progress">
                            <span style={{ width: `${coverage}%` }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Toolbar ────────────────────────────────────────── */}
            <div className="ld-toolbar">
                <div className="ld-field">
                    <label className="ld-field-label">
                        <i className="pi pi-building" /> Client
                    </label>
                    <Dropdown
                        value={selectedClient}
                        options={clients}
                        onChange={(e) => {
                            setSelectedClient(e.value);
                            setSelectedBranch(null);
                            setBranches([]);
                            fetchBranches(e.value);
                        }}
                        placeholder="Select Client"
                        filter
                        className="w-full"
                    />
                </div>

                <div className="ld-field">
                    <label className="ld-field-label">
                        <i className="pi pi-sitemap" /> Branch <span className="ld-muted">(segment)</span>
                        {branchesLoading && <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.75rem' }} />}
                    </label>
                    <Dropdown
                        value={selectedBranch}
                        options={branches}
                        onChange={(e) => setSelectedBranch(e.value)}
                        placeholder={branchesLoading ? 'Loading branches…' : 'Select Branch'}
                        filter
                        disabled={!selectedClient || branchesLoading}
                        className="w-full"
                    />
                </div>

                <div className="flex gap-2 align-items-center" style={{ paddingBottom: 1 }}>
                    {selectedBranch && (
                        <span className="ld-segment ld-hide-sm">
                            <i className="pi pi-link" />
                            {branchLabel}
                        </span>
                    )}
                    <Button label="Test All" icon="pi pi-bolt" className="ld-testall" onClick={runAll} loading={testingAll} disabled={!selectedBranch} />
                    <Button
                        label="Clear Results"
                        icon="pi pi-refresh"
                        className="ld-clear"
                        onClick={() => {
                            setResults({});
                            setSelectedClient(null);
                            setSelectedBranch(null);
                            setBranches([]);
                        }}
                        disabled={testingAll}
                    />
                </div>
            </div>

            {/* ── Split view ─────────────────────────────────────── */}
            <div className="ld-split">
                {/* Left: endpoint list */}
                <div className="ld-sidebar">
                    <div className="ld-search">
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search endpoints…" />
                        </span>
                    </div>

                    {Object.keys(groupedEndpoints).length === 0 && (
                        <div className="ld-empty-search">
                            <i className="pi pi-inbox" style={{ fontSize: '1.6rem', display: 'block', marginBottom: 8, color: '#cbd5e1' }} />
                            No endpoints match your search.
                        </div>
                    )}

                    {Object.entries(groupedEndpoints).map(([group, eps]) => {
                        const gs = groupStyle(group);
                        const isCollapsed = collapsedGroups.has(group);
                        return (
                            <div key={group}>
                                <div className="ld-group-header" style={{ background: gs.bg, color: gs.color, borderLeft: `4px solid ${gs.accent}` }} onClick={() => toggleGroup(group)}>
                                    <i className={gs.icon} />
                                    <span style={{ flex: 1 }}>{group}</span>
                                    <span className="ld-group-count">{eps.length}</span>
                                    <i className={`pi ${isCollapsed ? 'pi-chevron-right' : 'pi-chevron-down'}`} style={{ fontSize: '0.65rem' }} />
                                </div>
                                {!isCollapsed &&
                                    eps.map((ep) => {
                                        const info = statusInfo(ep);
                                        const r = results[ep.key];
                                        const isSelected = selectedEndpoint?.key === ep.key;
                                        return (
                                            <div key={ep.key} className={`ld-item${isSelected ? ' selected' : ''}`} style={{ borderLeftColor: isSelected ? gs.accent : 'transparent' }} onClick={() => selectEndpoint(ep)}>
                                                <div className="ld-item-row">
                                                    <Pill tone={ep.method.toLowerCase()}>{ep.method}</Pill>
                                                    <span className="ld-item-title">{ep.label}</span>
                                                    {runningKeys.has(ep.key) && <i className="pi pi-spin pi-spinner" style={{ color: gs.accent, fontSize: '0.8rem' }} />}
                                                </div>
                                                <div className="ld-item-foot">
                                                    <Pill tone={info.severity}>{info.text}</Pill>
                                                    <span className="ld-item-meta">{r ? `${r.duration} ms · ${r.testedAt}` : 'Not tested yet'}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        );
                    })}
                </div>

                {/* Right: detail pane */}
                <div className="ld-detail">
                    {selectedEndpoint ? (
                        <>
                            <div className="ld-detail-header" style={{ background: `linear-gradient(180deg, ${detailStyle.soft} 0%, rgba(255,255,255,0.9) 100%)` }}>
                                <div className="ld-crumb" style={{ color: detailStyle.color }}>
                                    <i className={detailStyle.icon} style={{ fontSize: '0.75rem' }} />
                                    <span>{selectedEndpoint.group}</span>
                                </div>
                                <div className="ld-detail-title">
                                    <Pill tone={selectedEndpoint.method.toLowerCase()}>{selectedEndpoint.method}</Pill>
                                    <h3>{selectedEndpoint.label}</h3>
                                </div>
                                <div className="ld-detail-path">/{selectedEndpoint.path}</div>
                            </div>
                            <div className="ld-detail-body">{renderDetailBody()}</div>
                        </>
                    ) : (
                        <div className="ld-empty">
                            <div className="ld-empty-icon">
                                <i className="pi pi-send" />
                            </div>
                            <h4>No endpoint selected</h4>
                            <p>Pick an endpoint from the list to view its request URL, run it, and inspect the response.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Test SMS confirmation */}
            <Dialog
                header="Confirm SMS Test"
                visible={testSmsConfirm}
                style={{ width: '400px' }}
                onHide={() => setTestSmsConfirm(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setTestSmsConfirm(false)} />
                        <Button
                            label="Yes, Test SMS"
                            icon="pi pi-check"
                            className="p-button-success"
                            onClick={() => {
                                setTestSmsConfirm(false);
                                runEndpoint(selectedEndpoint);
                            }}
                        />
                    </div>
                }
            >
                <div className="flex align-items-center gap-3">
                    <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: '2.5rem' }} />
                    <span>
                        Are you sure you want to test the <strong>Sales Tracking Records</strong> SMS endpoint?
                        <br />
                        <small style={{ color: '#888' }}>This will make a live request to the server.</small>
                    </span>
                </div>
            </Dialog>

            {/* Reset Masters confirmation */}
            <Dialog
                header="Reset All Masters"
                visible={resetVisible}
                style={{ width: '420px' }}
                onHide={() => setResetVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setResetVisible(false)} disabled={resetLoading} />
                        <Button label="Reset Masters" icon="pi pi-database" className="p-button-danger" onClick={resetMasters} loading={resetLoading} />
                    </div>
                }
            >
                <div className="flex align-items-center gap-3">
                    <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: '2.5rem' }} />
                    <span>
                        Are you sure you want to reset all masters for branch <strong>{branches.find((b) => b.value === selectedBranch)?.label}</strong>?
                        <br />
                        <small style={{ color: '#888' }}>After reset, run "Test All" to verify each endpoint and payload.</small>
                    </span>
                </div>
            </Dialog>
        </div>
    );
}
